import { WalletTransactionType, useTransaction } from "@/api/transactions"
import Loader from "@/components/layout/loader"
import { format } from "date-fns"
import { Link } from "@/components/ui/link"
import LinkCircleIcon from "@/components/icons/link-circle"
import PaperFoldTextIcon from "@/components/icons/paper-fold-text"
import { formatInvoiceNumber } from "@/api/invoices"
import PaymentDetailsItem from "./payment-details-item"
import NetworkBadge from "@/components/ux/network.badge"
import { shortenString } from "@/lib/utils"
import TokenBadge from "@/components/ux/token.badge"
import config from "@/config"
import { Network } from "@/config/tokens"
import ButtonCopy from "@/components/ui/button-copy"
import { Timeline, TimelineItem } from "@/components/ux/timeline"
import { DetailsSheet } from "@/components/ux/details.sheet"

interface PaymentDetailsSheetProps {
  id: string;
  onClose: (open: boolean) => void;
}

export function PaymentDetailsSheet({ id, onClose }: PaymentDetailsSheetProps) {
  const { walletTransaction } = useTransaction(id)

  return (
    <DetailsSheet title="Payment Details" onClose={onClose}>
      {!walletTransaction && <Loader message="Loading payment details..." />}
      {walletTransaction && (
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="text-xl font-medium">
              {walletTransaction.payment &&
                walletTransaction.payment.invoice && (
                  <div>
                    {walletTransaction.payment.invoice.customer.firstName}{" "}
                    {walletTransaction.payment.invoice.customer.lastName}
                  </div>
                )}
              {walletTransaction.payment &&
                walletTransaction.payment.paymentLink && (
                  <div>{walletTransaction.payment.name}</div>
                )}
            </h1>
            <div className="text-sm text-gray-500">
              Payment Completed on{" "}
              {format(walletTransaction.transaction.createdAt, "MMM do, yyyy")}
            </div>
          </div>
          <div className="flex flex-row gap-2 items-center font-semibold text-lg">
            ${walletTransaction.transaction.amount}{" "}
            {walletTransaction.transaction.token}
          </div>
          <div className="flex flex-col py-2 border-t border-b">
            <h2 className="text-lg font-semibold">Timeline</h2>
          </div>
          <Timeline>
            <TimelineItem
              label="Payment Received"
              date={walletTransaction.transaction.createdAt}
            />
            {walletTransaction.payment && (
              <TimelineItem
                label="Payment Started"
                date={walletTransaction.payment.createdAt}
              />
            )}
          </Timeline>
          <div className="py-2 border-b">
            <h2 className="text-lg font-semibold">Payment Details</h2>
          </div>
          {walletTransaction.payment &&
            walletTransaction.type ===
              WalletTransactionType.PAYMENT_RECEIVED && (
              <PaymentDetailsItem
                label={
                  walletTransaction.payment.paymentLink
                    ? "Payment Link"
                    : "Invoice"
                }
              >
                {walletTransaction.payment.paymentLink && (
                  <Link
                    to={`/payment-links/${walletTransaction.payment.paymentLink.id}`}
                  >
                    <LinkCircleIcon />
                    {walletTransaction.payment.paymentLink.title}
                  </Link>
                )}
                {walletTransaction.payment.invoice && (
                  <Link
                    to={`/invoices/${walletTransaction.payment.invoice.id}`}
                  >
                    <PaperFoldTextIcon />#
                    {formatInvoiceNumber(
                      walletTransaction.payment.invoice.number,
                    )}
                  </Link>
                )}
              </PaymentDetailsItem>
            )}
          <PaymentDetailsItem label="Token">
            <TokenBadge token={walletTransaction.transaction.token} />
          </PaymentDetailsItem>
          <PaymentDetailsItem label="Network">
            <NetworkBadge network={walletTransaction.transaction.network} />
          </PaymentDetailsItem>
          <PaymentDetailsItem label="Transaction ID">
            <ButtonCopy value={walletTransaction.transaction.hash} />
            <a
              href={config.networks[
                walletTransaction.transaction.network as Network
              ].explorerUrl(walletTransaction.transaction.hash)}
              target="_blank"
              rel="noreferrer noopener"
            >
              {shortenString(walletTransaction.transaction.hash, 10)}
            </a>
          </PaymentDetailsItem>
          <PaymentDetailsItem label="From Address">
            <ButtonCopy value={walletTransaction.transaction.fromAddress} />
            {shortenString(walletTransaction.transaction.fromAddress, 10)}
          </PaymentDetailsItem>
        </div>
      )}
    </DetailsSheet>
  )
}
